import Article from "./Article";
import { UnitConverter } from '@vibialighting/vibia-utils';
import BaseBOM from "./BaseBOM";
import ArticleCollection, { getBov } from "./ArticleCollection";
import { convertJSONtoBOM } from "../converters/ProjectConverter";
import Space from "../composition/Space";
import Toggles from "@domain/toggles/Toggles"
import Composition from "../composition/Composition";
import Drivers from "../data/Drivers";
import { assignLightsByAccessoryCapacity } from "../converters/ElectricalGroup"
import { isModelComposingPendants, pendantHeightToCm } from "../utils/utils";
import { accessoriesSubfamiliesExcludedInDomain, accessoryProductTypology, PRODUCT_CATEGORIES, PRODUCT_SUBCATEGORIES, PRODUCT_SUBCATEGORIES_OBJECT, SUBFAMILIES_ID } from "../data/Constants"


const repeat = (arr, n) => Array(n).fill(arr).flat();

const toolkitCategory = {
    id: 6,
    description: "TOOLKIT"
}

export default class CreationBOM extends BaseBOM {

    // Builds an empty, valid CREATION BOM ready to be filled by a consumer.
    // Its props round-trip through convertJSONtoBOM(bom.getProps()) without losing anything
    // structural. It has no articles yet, so pricing/order helpers return empty results until
    // compositions are added.
    static empty(): CreationBOM {
        return new CreationBOM({
            type: "CREATION",
            compositions: [],
            quantity: 1,
            validation: { status: 0 }
        })
    }

    getOrderReferences(articleCollection?: ArticleCollection) {
        const compositions = this.getProps().compositions;
        let articles = []
        for (const composition of compositions) {
            articles = [...articles, ...(Array.isArray(composition.articles) ? composition.articles : [])]
        }
        let articleQuantities = {}
        for (const article of articles) {
            const reference = article.referenceCode
            articleQuantities[reference] = articleQuantities[reference] ? articleQuantities[reference] + 1 : 1
        }
        const result = []
        for (const referenceQuantity of Object.entries(articleQuantities)) {
            const [reference, quantity] = referenceQuantity as [string, number]
            const quantityTotal = quantity * this.getProps().quantity
            const leadtime = articleCollection ? this.getReferenceLeadtime(articleCollection, reference, quantityTotal) : null
            const moq = articleCollection ? this.getReferenceMoq(articleCollection, reference) : null
            result.push({
                quantity: quantityTotal,
                reference,
                leadtime,
                moq
            })
        }
        return result
    }

    getModels() {
        const compositions = this.getProps().compositions;
        let articles = []
        for (const composition of compositions) {
            articles = [...articles, ...(Array.isArray(composition.articles) ? composition.articles : [])]
            if (Array.isArray(composition.accessories)) {
                articles = [...articles, ...composition.accessories]
            }
        }

        let modelsDict = {}
        for (const article of articles) {
            if (
                accessoriesSubfamiliesExcludedInDomain.includes(article?.subFamily?.id)
                && article?.articleType?.level1?.id === accessoryProductTypology.id
            ) {
                continue;
            }

            const reference = article.model
            modelsDict[reference] = true
        }
        return Object.keys(modelsDict)
    }

    getAllocatedCodes(): any[] {
        if (this.props.creationCode) {
            return [this.props.creationCode]
        } else {
            return []
        }
    }

    refreshCodes(articleCollection, allocatedCodes, counters, calcCounter = true) {

        let counterLW = counters.LW || 0;
        let counterCR = counters.CR || 0;

        const allocated = allocatedCodes.filter(code => code.includes('CR')) // Filter those that contain CR
        const { code, number, counter } = BaseBOM.calculateCode('CR', allocated, counterCR, calcCounter);
        this.props.creationCode = code
        this.props.creationNumber = number

        counterCR = counter;

        for (let i = 0; i < this.props.compositions.length; i++) {
            for (let j = 0; j < this.props.compositions[i].articles.length; j++) {
                let articleIterator = convertJSONtoBOM({
                    ...this.props.compositions[i].articles[j],
                    type: 'REGULAR'
                }).getArticles(articleCollection)[0]
                if (articleCollection.articleNeedsLWCode(articleIterator)) {
                    const allocated = allocatedCodes.filter(code => code.includes('LW'))
                    const { code } = BaseBOM.calculateCode(`${articleIterator.getModel()}LW`, allocated, counterLW)
                    this.props.compositions[i].articles[j].articleCode = code;
                    counterLW++
                }
            }
        }

        return { BOM: this, counters: { ...counters, CR: counterCR, LW: counterLW } }
    }

    // TODO: To increase efficiency, we should group exact same articles and change the contract from:
    // Article[] to: {article: Article, quantity: number} []
    getArticles(articleCollection?: ArticleCollection) {
        let articles = []

        for (const composition of this.props.compositions || []) {
            for (const product of composition.articles || []) {
                articles = [
                    ...articles,
                    ...convertJSONtoBOM({
                        ...product,
                        type: 'REGULAR'
                    }).getArticles(articleCollection)
                ]
            }
        }

        articles = repeat(articles, this.props.quantity)
        return articles
    }

    // Number of compositions in this creation.
    getCompositionsCount(): number {
        return this.props.compositions?.length ?? 0
    }

    // Total luminaires across every composition — the sum of each article's quantity. Used by the
    // project detail card summary ("N luminaires · N compositions") for a CREATION product.
    getTotalLuminaires(): number {
        return (this.props.compositions ?? []).reduce((total, composition) => {
            const perComposition = (composition.articles ?? []).reduce(
                (sum, article) => sum + (article.quantity ?? 0),
                0,
            )
            return total + perComposition
        }, 0)
    }

    getSublevel() {
        let sublevel;
        const compositions = this.getProps().compositions;
        if (compositions && compositions.length > 0) {
            const firstComposition = compositions[0];

            if (firstComposition?.articles && firstComposition.articles.length > 0) {
                sublevel = firstComposition.articles[0]?.sublevel?.id;
            }

            if (!sublevel && firstComposition?.accessories && firstComposition.accessories.length > 0) {
                sublevel = firstComposition.accessories[0]?.sublevel?.id;
            }
        }
        return sublevel;
    }

    static groupCompositionArticles(compositionArticles) {
        const groupedCompositionArticles = []

        compositionArticles.forEach(compositionArticle => {
            const articleToUpdate = groupedCompositionArticles.find(groupedArticle => {
                const isSameArticleCode = compositionArticle.articleCode === groupedArticle.articleCode
                const isSamePendantHeight = groupedArticle.attributes?.pendantHeight?.value === compositionArticle.attributes?.pendantHeight?.value

                return isSameArticleCode && isSamePendantHeight
            })

            const updateQuantity = articleToUpdate

            if (updateQuantity) {
                articleToUpdate.quantity++
            } else {
                compositionArticle.quantity = 1
                groupedCompositionArticles.push(compositionArticle)
            }
        })
        return groupedCompositionArticles
    }

    static parseArticlesToComposition(articles: Article[], index: number, composition?: Composition, configuratorType?) {

        let totalWatts = 0;
        let totalLumensLed = 0;
        let compositionArticles = [];
        let compositionAccesories = []
        const articlesWithoutAccesories = articles.filter(article => !article.isAccessory())
        const articlesAccessories = articles.filter(article => article.isAccessory())
        const articleIsCP = articlesWithoutAccesories.every(article => isModelComposingPendants(article.getModel()));
        let isComposingPendants = articleIsCP;
        let attributes: any = {};

        let attributesKeyExcluded = [];

        let pendantHeight = 0;
        for (const article of articles) {
            if (article.getAttribute('pendantHeight') > pendantHeight) {
                pendantHeight = article.getAttribute('pendantHeight')
            }
            const articleAttributes = article.getProps().attributes;
            for (const attributeKey of Object.keys(articleAttributes)) {
                if (!articleAttributes[attributeKey].id) {
                    continue;
                }

                if (!attributes[attributeKey]) {
                    attributes[attributeKey] = articleAttributes[attributeKey];
                } else if (attributes[attributeKey] && attributes[attributeKey].id !== articleAttributes[attributeKey].id) {
                    attributesKeyExcluded.push(attributeKey);
                }
            }
            totalWatts += article.getProps().watts.value;
            totalLumensLed += article.getProps().lumensLed?.value || null;
            const parsedArticle: any = CreationBOM.parseArticleProperties(article);
            parsedArticle.articleCode = parsedArticle.referenceCode;
            if (Toggles.isEnabled("addAccessoryCodeToAccessories") && article.isAccessory()) {
                parsedArticle.accessoryCode = parsedArticle.articleCode
            }
            parsedArticle.quantity = 1;
            parsedArticle.isAvailablePrice = 1;
            parsedArticle.isPersonalized = +article.isPersonalized();
            if (isComposingPendants) {
                const avoidAllLightsHaveNoDriverOption = Toggles.isEnabled('dontCalcPriceAccesorieWhenCP') ? true : composition?.allLightsHaveNoDriverOption()
                if (composition && avoidAllLightsHaveNoDriverOption && composition.allLightsHaveNoDriver()) {
                    if (articlesAccessories.length > 0 && Toggles.isEnabled('dontCalcPriceAccesorieWhenCP')) {
                        const psaAccessoryControlAttribute = articlesAccessories[0].getAttribute("control", true)
                        composition.setAttribute("control", psaAccessoryControlAttribute)
                    }
                    let reference = parsedArticle.referenceCode
                    let controlModifier = composition.getAttributeModifier("control")
                    parsedArticle.priceReference = reference.replace("M", controlModifier)
                }
            }

                parsedArticle.type = "REGULAR" // This always asumes all articles are regular (Maybe will change with PSAs are considered)
                parsedArticle.attributes = articleAttributes

            delete parsedArticle.hasAttributes;


            if (article.isAccessory()) {
                compositionAccesories.push(parsedArticle)
            } else {
                compositionArticles.push(parsedArticle)
            }
        }

        compositionArticles = this.groupCompositionArticles(compositionArticles)
        compositionAccesories = this.groupCompositionArticles(compositionAccesories)


        for (const attributeKeyExcluded of attributesKeyExcluded) {
            delete attributes[attributeKeyExcluded];
        }


        if (composition && composition.isRemote()) {
            const newCompositionControl = composition.getAttributeVariation("control")
            if (newCompositionControl) {
                const parsedNewCompositionControl = {
                    description: newCompositionControl.description,
                    code: newCompositionControl.modifier,
                    bov: getBov(newCompositionControl.type)
                }
                attributes.control = parsedNewCompositionControl
            } else {
                attributes.control = null
            }
        }


        let subFamily = compositionArticles[0]?.subFamily
        let familyId = compositionArticles[0]?.familyId
        subFamily = subFamily || compositionAccesories[0].subFamily
        familyId = familyId || compositionAccesories[0].familyId

        let BOM = {
            compositionNumber: index + 1,
            subFamily: subFamily,
            familyId: familyId,
            attributes,
            totalLumensLed: UnitConverter.toUdObject(
                totalLumensLed,
                UnitConverter.UD_TYPES.LUMENS
            ),
            totalWatts: UnitConverter.toUdObject(
                totalWatts,
                UnitConverter.UD_TYPES.WATTS
            ),
            category: {
                id: 1,
                description: "REGULAR",
                subcategory: {} as any
            },
            articles: compositionArticles,
            accessories: compositionAccesories,
            electricalGroups: [],
            packaging: {
                packages: [],
                timing: []
            },
            assemblyData: [{}],
        };
        delete BOM.electricalGroups
        delete BOM.category.subcategory

        if (Toggles.isEnabled('dontCalcPriceAccesorieWhenCP')) {
            if (composition && composition.isRemote() && configuratorType?.id !== 6) {
                const accessories = composition.getDriver()
                const catalog = articles[0].getCatalog() == 21 ? 'usa' : 'row'
                const parsedAccessories = Drivers.parseToAccessories(accessories?.[catalog])
                BOM.accessories = [...BOM.accessories, ...parsedAccessories]
            }
        } else {
            if (composition && composition.isRemote()) {
                const accessories = composition.getDriver()
                const catalog = articles[0].getCatalog() == 21 ? 'usa' : 'row'
                const parsedAccessories = Drivers.parseToAccessories(accessories?.[catalog])
                BOM.accessories = [...BOM.accessories, ...parsedAccessories]
            }
        }


        if (BOM.accessories && BOM.accessories.length > 0) {
            // TOOLKIT
            BOM.category.id = 6
            BOM.category.description = "TOOLKIT"
        } else {
            // REGULAR
            BOM.category.id = 1
            BOM.category.description = "REGULAR"
        }

        const isToolkit = BOM.category.id === 6
        const allArticlesFromCP = isToolkit ?
            articlesWithoutAccesories.every(article => isModelComposingPendants(article.getModel())) :
            articles.every(article => isModelComposingPendants(article.getModel()))

        if (allArticlesFromCP) {
            const CPSubcategory = PRODUCT_SUBCATEGORIES_OBJECT.find(PRODUCT_SUBCATEGORY => PRODUCT_SUBCATEGORY.id === PRODUCT_SUBCATEGORIES.COMPOSING_PENDANTS)
            BOM.category.subcategory = { ...CPSubcategory }
        }


        const catalog = articles[0].getCatalog() == 21 ? 'usa' : 'row'
        if (composition && composition.isRemote()) {
            BOM.electricalGroups = assignLightsByAccessoryCapacity(composition, catalog)
        }

        if (pendantHeight) {
            const catalogId = articles[0].getProps().catalog.id;
            BOM.attributes.pendantHeight = UnitConverter.toObjectLengthByCatalog(
                catalogId,
                pendantHeightToCm(pendantHeight, catalogId),
                UnitConverter.UD_TYPES.CENTIMETERS
            );
        }

        return BOM;
    }


    static fromArticles(articles: Array<Article>, space?: Space) {
        const articlesByGroup: { [key: string]: Array<Article> } = CreationBOM.groupArticles(articles, space)

        let domainCompositions = space.getCompositions()
        const configuratorType = BaseBOM.getConfiguratorType(space)
        const compositions = Object.entries(articlesByGroup).map(([key, value], index) => {
            const composition = domainCompositions.find((comp) => comp.getId() == key)
            return CreationBOM.parseArticlesToComposition(value, index, composition, configuratorType);
        });

        let applicationLocation = compositions[0].articles[0]?.applicationLocation
        applicationLocation = applicationLocation || compositions[0].accessories[0].applicationLocation

        const productTotalWatts = compositions.reduce((sum, c) => sum + (c.totalWatts?.value ?? 0), 0)
        const productTotalLumensLed = compositions.reduce((sum, c) => sum + (c.totalLumensLed?.value ?? 0), 0)

        const BOM = {
            applicationLocation: applicationLocation,
            configuratorType,
            compositions,
            type: "CREATION",
            quantity: 1,
            totalWatts: UnitConverter.toUdObject(+productTotalWatts.toFixed(2), UnitConverter.UD_TYPES.WATTS),
            totalLumensLed: UnitConverter.toUdObject(+productTotalLumensLed.toFixed(2), UnitConverter.UD_TYPES.LUMENS),
            isPersonalized: articles.every(a => a.isPersonalized()) ? 1 : 0,
            validation: {
                status: 0
            },
            familyId: compositions[0].familyId,
            subFamily: {
                ...compositions[0].subFamily,
                prefixCode: "CR"
            },
            category: {
                id: 7,
                description: "COMPOSING_PENDANTS"
            }
        }


        const areAllCompositionsSameCategory = (compositions: Array<any>): boolean => {
            const firstCompositionCategory = compositions[0].category
            return compositions.every(composition => composition.category.id === firstCompositionCategory.id)
        }
        const sameCategoryCompositions = areAllCompositionsSameCategory(BOM.compositions)
        let creationCategory = { ...compositions[0].category }

        if (!sameCategoryCompositions) {
            const prioritizedCategory = PRODUCT_CATEGORIES.TOOLKIT
            const prioritizedComposition = BOM.compositions.find(composition => composition.category.id === prioritizedCategory)
            const priorityCategory = prioritizedComposition?.category
            if (priorityCategory) {
                creationCategory = { ...priorityCategory }
            }
        }

        BOM.category = creationCategory


        return new CreationBOM(BOM)
    }

    static groupArticles(articles: Article[], space: Space): { [key: string]: Article[]; } {
        return articles.reduce((acc, item) => {
            let id = item.getProps().grandparentId
            const isComplement = item.isAccessory()

            if (isComplement) {
                id = item.getProps().parentId
            }

            if (id === space.getId()) {
                return {
                    ...acc,
                    [item.getProps().id]: [item]
                };
            }

            if (acc[id]?.length) {
                return {
                    ...acc,
                    [id]: [
                        ...acc[id],
                        item
                    ]
                };
            }

            return {
                ...acc,
                [id]: [item]
            }

        }, {})
    }
}